import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { trigger, style, animate, transition } from '@angular/animations';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { skip } from 'rxjs/operators';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subscription } from 'rxjs';
import { AppSettings } from 'src/app/app-settings';
import { CategoriesListComponent } from 'src/app/components/bloques/categories-list/categories-list.component';
import { CropperImageComponent } from 'src/app/components/bloques/cropper-image/cropper-image.component';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { ApiBusinessService } from 'src/app/services/api-business.service';
import { CiudadesService } from 'src/app/services/ciudades.service';
import { CreateEventService } from 'src/app/services/create-event.service';
import { GooglePlacesService } from 'src/app/services/google-places.service';
import { PlacesService } from 'src/app/services/places.service';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { log } from 'console';

@Component({
  selector: 'app-create-event',
  templateUrl: './create-event.component.html',
  styleUrls: ['./create-event.component.scss'],
  animations: [
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden', transform: 'translateY(-6px)' }),
        animate('220ms ease-out', style({ opacity: 1, height: '*', transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('180ms ease-in', style({ opacity: 0, height: 0, transform: 'translateY(-6px)' }))
      ])
    ])
  ]
})
export class CreateEventComponent {
  event: any = {};
  submenuActive: any = 1;
  titleTab: string = 'Paso 1: Información del evento';
  categoriesSelected: any[] = [];
  urlImages: string = AppSettings.MEDIA_URI + '/events_images/';
  urlImagesVenues: string = AppSettings.MEDIA_URI + '/spaces/';
  imgControl: FormControl = new FormControl(null);
  changedImg: boolean = false;
  @Input() textArea: any = '';
  @Input() title: any = '';
  @Input() otherAddress: boolean = false;
  @Input() onlineEvent: boolean = false;
  @Input() otherAddresString: any = '';
  @Input() eventType: 'informative' | 'external' | 'normal' | null = null;
  @Input() idEvent!: number;
  // Cuando se edita desde el panel de control, solo se muestra esta tarjeta
  // interna ('info' | 'lugar' | 'config' | 'multimedia'); null = mostrar las 4
  // (comportamiento normal del creador).
  @Input() onlyCard: string | null = null;
  // true solo cuando se entra a editar un evento YA existente desde el panel de
  // control (ruta '/detail/...'); false durante el alta inicial, aunque el evento
  // ya se haya guardado como borrador varias veces en la misma sesión de creación.
  @Input() embedded: boolean = false;

  // Canales de venta
  canalesOnline: boolean = true;
  canalesTaquilla: boolean = false;
  remountMapSeats: boolean = true;

  // 'informative' (sin venta) y 'external' (venta gestionada fuera de Citylok) son
  // mutuamente excluyentes entre sí y con 'normal' (venta de entradas en Citylok).
  applyEventType(type: 'informative' | 'external' | 'normal' | null) {
    if (type === 'informative') {
      this.eventInfo.patchValue({ informative: true, external: false, only_tpv_sales: false });
    } else if (type === 'external') {
      this.eventInfo.patchValue({ informative: false, external: true, only_tpv_sales: false });
      // Venta externa: sin espacio asociado ni aforo, solo dirección libre.
      if (!this.otherAddress) {
        this.selectOtherAddressMode();
      }
    } else {
      this.eventInfo.patchValue({ informative: false, external: false });
    }
  }

  // Canales de venta: excluyentes, no combinables. El backend solo guarda un
  // booleano (only_tpv_sales) y taquilla ya puede vender siempre que la web no
  // esté bloqueada, así que no existe un estado real de "ambos a la vez" ni de
  // "solo web, taquilla bloqueada" (ver btns-event-totem.component.html, que
  // desactiva la compra web justamente cuando only_tpv_sales es true).
  selectSalesChannel(onlyTpvSales: boolean) {
    this.canalesTaquilla = onlyTpvSales;
    this.canalesOnline = !onlyTpvSales;
    this.eventInfo.patchValue({ only_tpv_sales: onlyTpvSales });
  }

  hashtags: any[] = [];

  // ─── Estado de completado de secciones (check verde) ───────────
  get infoSectionComplete(): boolean {
    const v = this.eventInfo?.value || {};
    return !!(v.title && v.title.trim())
      && (this.categoriesSelected?.length > 0)
      && !!(this.imgControl?.value || v.file)
      && !!v.description
      && (this.eventType !== 'external' || !!v.external_url);
  }

  get lugarSectionComplete(): boolean {
    const v = this.eventInfo?.value || {};
    // El lugar solo se exime para eventos de venta externa (la venta ocurre fuera de Citylok)
    const hasLocation = !!(this.space || v.address || v.online || v.external);
    if (!hasLocation) return false;
    // Aforo: no es obligatorio con mapa de butacas, ni en eventos informativos o externos
    if (v.use_map || v.informative || v.external) return true;
    const capacity = this.createEvent.getCapacity();
    return capacity != null && capacity > 0;
  }

  // Start and End Date
  startStringDate!: any;
  endStringDate!: any;
  startDate!: NgbDate;
  endDate!: NgbDate;
  typeDatepicker!: string;

  dates!: any;

  space!: any;

  placeList: any[] = [];

  placeOptions: { id: any; label: string; imageUrl: string }[] = [];

  payload: any = {};

  // Options days
  repeatWeek: any[] = [
    { day: 'L', number: 1, text: 'Lunes' },
    { day: 'M', number: 2, text: 'Martes' },
    { day: 'X', number: 3, text: 'Miércoles' },
    { day: 'J', number: 4, text: 'Jueves' },
    { day: 'V', number: 5, text: 'Viernes' },
    { day: 'S', number: 6, text: 'Sábados' },
    { day: 'D', number: 0, text: 'Domingos' },
  ];
  repeatMonth: any[] = [];
  repeatDays: any[] = [];

  selectRepeat: any[] = []; // Selected repeat days
  repeatDate!: NgbDate; // If repeat end on exact date
  repeatStringDate!: string;

  selectedSchedules: any[] = [];
  selectedSchedulesRepeat: any[] = [];
  infoRep: string = '';

  maxType: number = 54;
  profile!: any;
  dateOld!: any;
  now!: any;

  idBussiness: any;

  public isEmojiPickerVisible: boolean;
  public addEmoji(event) {
    this.isEmojiPickerVisible = false;
    return event.emoji.native;
  }

  editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: 'auto',
    minHeight: '250px',
    maxHeight: 'auto',
    width: 'auto',
    minWidth: '0',
    translate: 'yes',
    enableToolbar: true,
    showToolbar: true,
    placeholder: 'Escribe una descripción del evento',
    defaultParagraphSeparator: 'p',
    defaultFontName: 'Satoshi-Regular',
    defaultFontSize: '',
    uploadUrl: 'v1/image',
    uploadWithCredentials: false,
    sanitize: true,
    toolbarPosition: 'top',
    toolbarHiddenButtons: [
      [
        'undo',
        'redo',
        'bold',
        'italic',
        'underline',
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyLeft',
        'justifyCenter',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertUnorderedList',
        'insertOrderedList',
        'heading',
        'fontName',
      ],
      [
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode',
      ],
    ],
  };

  eventInfo: FormGroup;
  croppedImage!: any;
  extraFileControl!: any;
  @Output() changeTab: EventEmitter<any> = new EventEmitter<any>();
  @Output() changePlaceEmit: EventEmitter<any> = new EventEmitter<any>();
  loading: boolean = true;

  lugar: any = '';
  searchResults: any[] = [];
  private mapSub!: Subscription;
  @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

  // Private Event
  private_event: boolean = false;
  private_event_code!: any;

  extraImages: any[] = [];
  openAdvancedConfig: boolean = false;
  private readonly allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  openCards: {[key: string]: boolean} = {
    'info': false,
    'lugar': false,
    'multimedia': false,
    'config': false
  };

  toggleCard(key: string) {
    this.openCards[key] = !this.openCards[key];

    // Si se abre la tarjeta "Lugar y aforo" con "Otra dirección" ya seleccionada
    // (p. ej. al editar un evento), el div #maplock acaba de aparecer en el DOM
    // y aún no tiene mapa inicializado.
    if (key === 'lugar' && this.openCards[key] && this.otherAddress) {
      setTimeout(() => this.googlePlacesService.openMap());
    }
  }

  openCard(key: string) {
    this.openCards[key] = true;

    // Igual que en toggleCard: si se abre "lugar" directamente (p. ej. al
    // navegar a esta pestaña desde router-create) con "Otra dirección" ya
    // activa, el div #maplock acaba de aparecer y necesita inicializar el mapa.
    if (key === 'lugar' && this.otherAddress) {
      setTimeout(() => this.googlePlacesService.openMap());
    }
  }

  expandAll() {
    Object.keys(this.openCards).forEach(key => this.openCards[key] = true);
  }

  // Al editar desde el panel de control, solo se muestra (y despliega) la
  // tarjeta indicada por onlyCard; el resto se ocultan por completo.
  isCardVisible(key: string): boolean {
    return !this.onlyCard || this.onlyCard === key;
  }

  isCardOpen(key: string): boolean {
    return this.onlyCard ? true : !!this.openCards[key];
  }

  // Sesiones del evento, usadas por app-map-seats-create para el selector de
  // bloqueo de asientos por sesión al editar un evento con mapa de butacas.
  sessions: any[] = [];

  // Versión del mapa de butacas con la que se guardó el evento (puede diferir de
  // space.current_version si el espacio se ha vuelto a editar después de crear
  // el evento). Solo tiene valor al editar un evento existente.
  eventMapVersion: any = null;

  // La empresa a la que se atribuyen los gastos de gestión solo la configura Citylok.
  isSuperAdmin: boolean = false;

  constructor(
    public dialog: MatDialog,
    private placeService: PlacesService,
    private createEvent: CreateEventService,
    private formBuilder: FormBuilder,
    private apiBusiness: ApiBusinessService,
    private googlePlacesService: GooglePlacesService,
    private ciudadService: CiudadesService,
    private cdr: ChangeDetectorRef,
    private auth: AuthenticationService,
  ) {
    if (localStorage.getItem('business')) {
      this.idBussiness = JSON.parse(localStorage.getItem('business')).id;
    }
    this.isSuperAdmin = this.auth.isSuperAdmin();
  }

  ngOnInit() {
    let business = JSON.parse(localStorage.getItem('business') || '{}');
    this.eventInfo = this.formBuilder.group({
      //Info basica
      id_company: [business.id],
      title: ['', Validators.required],
      description: [''],
      categories: [[]],
      cropped: [], //Imagen
      file: [''], //Imagen

      //Sesiones
      horarios: [[]],
      fechas: [[]],
      sessions: [[]],
      start_date: [null],
      end_date: [],

      //Lugar del evento
      online: [false],
      space: [],
      ciudad: [],
      latitude: [],
      longitude: [],
      address: [],
      use_map: [false], // Usar mapa de butacas o sin numerar
      id_city: [],
      id_province: [],
      city_name: [],
      province_name: [],
      country_name: [],

      //Tipos de evento
      external: [false],
      external_url: [],
      only_tpv_sales: [false],
      informative: [false],
      private_event: [false],
      private_event_code: [],

      //Facturación externa
      external_billing: [false],
      invoice_business_cif: [],
      invoice_business_name: [],
      invoice_business_address: [],
      //Empresa de los gastos de gestión
      external_fee_billing: [false],
      fee_invoice_business_cif: [],
      fee_invoice_business_name: [],

      //Precio "Desde" personalizado (solo edición, ver payload.custom_from_price en router-create.component.ts)
      useCustomPrice: [false],
      customFromPrice: [null],

      extraImages: this.extraImages,
      placeMap: [true],

      extraFile: [null],
      draft: [true],
      active: [false],
      infoDates: [],
      infoHours: [],
      entradas: [],
      mapEvent: [],
      hiddenEvent: [false],
      hashtags: [],
      extraLink: [],
      youtube_url: ['', (control: AbstractControl) =>
        this.createEvent.isValidYoutubeUrl(control.value) ? null : { invalidYoutubeUrl: true }],
    });

    // Cargar lugares inicialmente si ya hay una empresa seleccionada
    if (this.idBussiness) {
      this.loadPlacesByCompany(this.idBussiness);
    }

    // Comprobar si cambia la empresa
    this.apiBusiness.businessStorage.subscribe({
      next: (value) => {
        this.loading = true;
        this.idBussiness = value?.id;
        if (this.idBussiness) {
          this.changePlace(null);
          this.loadPlacesByCompany(this.idBussiness);
        }
      },
    });
    this.getData();

    // Preconfigurar el tipo de evento según la selección previa
    this.applyEventType(this.eventType);

    // Reaccionar a cambios de tipo desde el padre sin destruir el formulario
    this.createEvent.eventType$.subscribe((type) => {
      if (!type) return;
      this.applyEventType(type);
    });

    this.eventInfo.valueChanges.subscribe((value) => {
      let payloadService = { valid: this.eventInfo.valid, values: value };
      this.createEvent.setInfoDetail(payloadService);
    });
  }

 ngAfterViewInit(): void {
    this.googlePlacesService.openMap();
    // skip(1) descarta la emisión inicial del BehaviorSubject (valor cacheado),
    // sin consumir la primera selección real del usuario.
    this.mapSub = this.googlePlacesService.mapsLocatorStorage.pipe(skip(1)).subscribe({
      next: (data) => {
        if (data) {
          if (data.address_components) {
            const objectCity = data.address_components.find((value: any) =>
              value.types.includes('locality'),
            );

            if (!objectCity) {
              this.openInfoCard(
                'La localización debe ser más específica (selecciona una ciudad o dirección exacta).',
                false,
                true,
              );
              this.lugar = '';
              this.eventInfo.patchValue({
                latitude: null,
                longitude: null,
                address: null,
                id_city: null,
                id_province: null,
                city_name: null,
                province_name: null,
                country_name: null,
              });
              return;
            }

            const objectProvince = data.address_components.find((value: any) =>
              value.types.includes('administrative_area_level_1'),
            );
            const objectCountry = data.address_components.find((value: any) =>
              value.types.includes('country'),
            );

            this.lugar = data;
            this.eventInfo.controls['latitude'].setValue(
              data.geometry?.location.lat(),
            );
            this.eventInfo.controls['longitude'].setValue(
              data.geometry?.location.lng(),
            );
            this.eventInfo.controls['address'].setValue(data.formatted_address);
            this.eventInfo.controls['city_name'].setValue(objectCity.long_name);
            this.eventInfo.controls['province_name'].setValue(objectProvince?.long_name || null);
            this.eventInfo.controls['country_name'].setValue(objectCountry?.long_name || null);

            this.ciudadService
              .getAllCitieByName(objectCity.long_name)
              .subscribe((res) => {
                if (res.data?.length > 0) {
                  const city = res.data[0];
                  this.eventInfo.controls['id_city'].setValue(city['id']);
                  const provinceId =
                    city['state_id'] ?? city['id_state'] ?? city['state']?.['id'] ?? null;
                  this.eventInfo.controls['id_province'].setValue(provinceId);
                } else {
                  this.eventInfo.controls['id_city'].setValue(null);
                  this.eventInfo.controls['id_province'].setValue(null);
                }
              });
          }
        }
      },
    });
  }


  onSearchPlaces() {
    // 1. Limpiamos los datos del formulario al escribir para obligar a usar el autocomplete
    this.eventInfo.patchValue({
      latitude: null,
      longitude: null,
      address: null,
      id_city: null,
    });

    if (this.lugar) {
      this.googlePlacesService.searchPlaces(this.lugar).then((results) => {
        this.searchResults = results;
      });
    } else {
      this.searchResults = [];
    }
  }

  onBlurAddress() {
    // Esperamos a que el optionSelected del mat-autocomplete actualice `lugar` a objeto
    // antes de validar. El blur se dispara antes que optionSelected en el DOM.
    setTimeout(() => {
      if (typeof this.lugar === 'string' && this.lugar.trim() !== '') {
        this.openInfoCard(
          'Debes seleccionar una dirección de las opciones sugeridas por el buscador.',
          false,
          true,
        );
        this.lugar = '';
        this.searchResults = [];
        this.eventInfo.patchValue({
          latitude: null,
          longitude: null,
          address: null,
          id_city: null,
          id_province: null,
          city_name: null,
          province_name: null,
          country_name: null,
        });
      }
    }, 250);
  }

  onSelectPlace() {
    this.eventInfo.controls['space'].setValue(null);
    this.googlePlacesService.selectPlace(this.lugar)

  }

  onSelectTextPlace(text: string) {
    this.googlePlacesService.searchPlaces(text).then((results) => {
      this.lugar = results[0];
      this.onSelectPlace();
    });
  }

  displayFn(location: any): string {
    if (location) {
      return location.description
        ? location.description
        : location.formatted_address;
    }
    return '';
  }

 getData() {
    let infoSaved = this.createEvent.getInfoEvent();
    if (infoSaved) {
      this.otherAddress = !!infoSaved.values.address;
      this.onlineEvent = infoSaved.values.online;
      this.categoriesSelected = infoSaved.values.categories || [];
      this.croppedImage = infoSaved.values.cropped;
      this.dates = infoSaved.values.infoDates;
      this.selectedSchedules = infoSaved.values.infoHours;
      this.space = infoSaved.values.space;
      this.sessions = infoSaved.values.sessions || [];
      this.eventMapVersion = infoSaved.values.map_version;
      
      this.imgControl.setValue(this.croppedImage);
      this.eventInfo.patchValue(infoSaved.values);
      
      // Validación correcta para mostrar el input de la dirección y rellenarlo
      if (infoSaved.values.address && !this.space && !this.onlineEvent) {
        this.otherAddress = true;
        this.lugar = infoSaved.values.address;
      } else {
        this.otherAddress = false;
      }
      
      this.hashtags = infoSaved.values.hashtags || [];
      this.private_event = infoSaved.values.private_event;
      this.private_event_code = infoSaved.values.private_event_code;
      this.extraImages = infoSaved.values.extraImages || [];

      // Restaurar canales de venta
      if (infoSaved.values.only_tpv_sales) {
        this.canalesOnline = false;
        this.canalesTaquilla = true;
      } else {
        this.canalesOnline = true;
        this.canalesTaquilla = false;
      }

      if (infoSaved.values.address && !infoSaved.values.space) {
        this.lugar = { formatted_address: infoSaved.values.address };
      }
    }
  }

  removeCategory(cat: any) {
    this.categoriesSelected = this.categoriesSelected.filter(c => c.id !== cat.id);
    this.eventInfo.controls['categories'].setValue(this.categoriesSelected);
  }

  openCategories() {
    const dialogRef = this.dialog.open(CategoriesListComponent, {
      panelClass: ['info-dialog', 'dialog-edit-lok', 'not-padding'],
      data: { categorySelect: this.categoriesSelected },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.categoriesSelected = result;
        this.eventInfo.controls['categories'].setValue(this.categoriesSelected);
      }
    });
  }

  private buildPlaceOptions(): void {
    this.placeOptions = this.placeList.map(place => ({
      id: place.id,
      label: place.name,
      imageUrl: this.urlImagesVenues + place.img_url
    }));
  }

  onPlaceSelected(id: any): void {
    const place = this.placeList.find(item => item.id === id) || null;
    this.changePlace(place);
  }

  changePlace(event: any) {
    if (event) {
      this.space = event;
      this.eventInfo.controls['space'].setValue(event);

      // Si el sitio tiene mapa (has_map) y una versión válida
      if (event.has_map && event.current_version) {
        // Por defecto sugerimos mapa de butacas si lo tiene
        this.eventInfo.controls['use_map'].setValue(true);
      } else {
        // Si no tiene mapa, forzamos "Sin numerar" (Aforo general)
        this.eventInfo.controls['use_map'].setValue(false);
      }

      let useMap = this.eventInfo.get('use_map').value;
      this.changePlaceEmit.emit({ space: event, useMapVenue: useMap });
      // Forzar remount de map-seats-create para que lea el nuevo venue
      this.remountMapSeats = false;
      setTimeout(() => this.remountMapSeats = true, 0);
    } else {
      this.space = null;
      this.eventInfo.controls['space'].setValue(null);
      this.eventInfo.controls['use_map'].setValue(false);
    }
  }

  changeUseMap() {
    const place = this.eventInfo.get('space').value;
    const useMap = this.eventInfo.get('use_map').value;
    this.changePlaceEmit.emit({ space: place, useMapVenue: useMap });
  }

  deleteExtraImg(item: number) {
    this.extraImages.splice(item, 1);
    this.eventInfo.patchValue({
      extraImages: this.extraImages,
    });
  }

  saveHashtags(hashtags: any) {
    this.eventInfo.controls['hashtags'].setValue(hashtags);
  }

  openInfoCard(
    msg: string,
    disabledClose: boolean = false,
    icon: boolean = true,
  ) {
    // Aseguramos cerrar el autocomplete antes de abrir el modal
    if (this.autocomplete) {
      this.autocomplete.closePanel();
    }
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: ['info-dialog'],
      disableClose: disabledClose,
      data: {
        icon: icon,
        text: msg,
        btnCancel: false,
      },
    });
  }

  openDatePicker(type: string) {
    if (this.typeDatepicker) {
      this.typeDatepicker = null;
      return;
    }
    this.typeDatepicker = type;
  }

  getDate(event: any, i: any) {
    this.dates = event;
    this.eventInfo.controls['sessions'].value[i].start_date = event;

    // this.eventInfo.controls['fechas'].setValue([event.fecha]);
    // this.eventInfo.controls['infoDates'].setValue(event);
    // this.eventInfo.controls['start_date'].setValue(event.fecha.fechaInicio + ' 00:00:00');
    // if(event.fecha.fechaFin){
    //   this.eventInfo.controls['end_date'].setValue(event.fecha.fechaFin + ' 23:59:59');
    // }
  }

  getSchedules(event: any) {
    this.selectedSchedules = event;
    this.eventInfo.controls['horarios'].setValue(event);
    this.eventInfo.controls['infoHours'].setValue(event);
  }

  isPlaceLocked(): boolean {
    // Solo se bloquea al reeditar un evento ya existente en una sesión posterior,
    // no durante el alta inicial (aunque ya se haya guardado el borrador).
    return !!(this.embedded && this.idEvent && this.space);
  }

  isCapacityTypeLocked(): boolean {
    // Igual que isPlaceLocked(): solo se bloquea al reeditar un evento ya
    // existente en una sesión posterior, no durante el alta inicial.
    return !!(this.embedded && this.idEvent);
  }

  selectSpaceMode() {
    if (!this.otherAddress) {
      return;
    }
    this.otherAddress = false;
    this.onlineEvent = false;
    this.eventInfo.controls['online'].setValue(false);
    this.eventInfo.controls['space'].setValue(null);

    // Si solo hay un espacio disponible, seleccionarlo automáticamente
    if (this.placeList.length === 1) {
      this.changePlace(this.placeList[0]);
    }
  }

  selectOtherAddressMode() {
    if (this.otherAddress) {
      return;
    }
    this.otherAddress = true;
    this.onlineEvent = false;
    this.space = null;
    this.eventInfo.controls['online'].setValue(false);
    this.eventInfo.controls['space'].setValue(null);
    this.eventInfo.controls['use_map'].setValue(false);
    this.changePlaceEmit.emit({ space: null, useMapVenue: false });

    // El div #maplock solo existe en el DOM cuando otherAddress es true, así que
    // el openMap() de ngAfterViewInit no lo encuentra si este modo se activa después.
    setTimeout(() => this.googlePlacesService.openMap());
  }

  isOnlineEvent() {
    if (this.onlineEvent) {
      this.otherAddress = false;
      this.space = null;
      this.otherAddresString = '';
      this.eventInfo.controls['online'].setValue(true);
    }
    this.eventInfo.controls['address'].setValue(null);
    this.eventInfo.controls['space'].setValue(null);
    this.eventInfo.controls['use_map'].setValue(false);
    this.changePlaceEmit.emit({ space: null, useMapVenue: false });
  }

  nextStep() {
    let step = { tab: 2, name: 'Sesiones', slug: 'sessions-event' };
    this.changeTab.emit(step);
  }

  addSession() {
    this.eventInfo.controls['sessions'].value.push({
      start_date: null,
      sale_start_date: null,
      sale_end_date: null,
      max_tickets_per_user: null,
    });
  }

  removeSession(i: number) {
    this.eventInfo.controls['sessions'].value.splice(i, 1);
  }

  loadPlacesByCompany(companyId: number) {
    this.placeService.getPlacesByCompany(companyId).subscribe({
      next: (data) => {
        // Solo los recintos que el usuario gestiona: si eligiera uno ajeno, el
        // back rechaza el alta con un 403 (ver SpaceAccess en la API).
        this.placeList = this.placeService.filterManagedPlaces(data.data.data, companyId);
        this.buildPlaceOptions();
        this.loading = false;

        // Forzar detección de cambios
        this.cdr.detectChanges();

        let infoSaved = this.createEvent.getInfoEvent();
        let placeSelect = infoSaved?.values?.space;
        if (placeSelect) {
          let place = this.placeList.find(
            (value) => value.id == placeSelect.id,
          );
          // Solo actualizamos si lo encontramos en la página cargada: si no,
          // dejamos el valor ya presente en el form (el local original del
          // evento) en vez de vaciarlo. Y usamos emitEvent: false porque esto
          // es solo rehidratar el estado ya conocido, no una edición real del
          // usuario; si no, dispara valueChanges -> setInfoDetail() y genera
          // un falso "cambios sin guardar" solo por abrir la pestaña de Info.
          if (place) {
            this.space = place;
            this.eventInfo.controls['space'].setValue(place, { emitEvent: false });
          }
        }

        // Si solo hay un espacio y no hay ninguna ubicación elegida aún
        // (por defecto "Espacio asociado" está seleccionado), lo autoseleccionamos.
        // OJO: `getPlacesByCompany` está paginado (15 por página) y no siempre incluye
        // el local original del evento (p.ej. si quedó fuera de la primera página o ya
        // no está activo). En ese caso `this.space` queda sin match aunque el evento SÍ
        // tenga un local guardado (`placeSelect`) — no hay que sustituirlo por otro local
        // distinto aquí, porque eso dispara changePlace()/getTickets() y vacía las
        // entradas y la configuración de mapa ya cargadas del evento real.
        if (!this.space && !placeSelect && !this.otherAddress && !this.onlineEvent && this.placeList.length === 1) {
          this.changePlace(this.placeList[0]);
        }
      },
      error: (err) => {
        console.error('Error loading places:', err);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  async onFileChange(event: any) {
    let files = event.target.files;

    if (files && files.length) {
      let file = files[0];
      const maxSize = 10 * 1024 * 1024; // 10 MB

      if (!this.allowedImageTypes.includes(file.type)) {
        this.openInfoCard(
          'Formato de imagen no admitido. <br> Usa JPG, PNG, WEBP o GIF.',
        );
        event.target.value = '';
        return;
      }

      try {
        file = await this.compressImage(file);
      } catch (e) {
        console.error('Error comprimiendo:', e);
      }

      if (file.size > maxSize) {
        this.openInfoCard(
          'La imagen o archivo no puede ocupar <br> más de 10 MB.',
        );
        event.target.value = '';
        return;
      }

      // Creamos un evento falso para el cropper sin dañar el DOM original
      const fakeEvent = {
        target: {
          files: [file],
        },
      };

      const dialogRef = this.dialog.open(CropperImageComponent, {
        panelClass: 'info-dialog',
        disableClose: true,
        // El cartel se pinta a 4:5 en la card y en la ficha del evento
        data: { img: fakeEvent, aspectRatio: 4 / 5 },
      });
      dialogRef.afterClosed().subscribe((result) => {
        event.target.value = '';
        if (result) {
          this.croppedImage = result.pathUrl;
          this.eventInfo.patchValue({
            file: result.object.base64,
            cropped: this.croppedImage,
          });
          this.imgControl.setValue(this.croppedImage);
        }
      });
    }
    if (!files || files.length === 0) {
      this.eventInfo.controls['file'].reset();
      this.croppedImage = '';
    }
  }

  async onExtraImg(event: any) {
    const files: File[] = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length) {
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10 MB
    let skippedUnsupportedFormat = false;

    for (let file of files) {
      if (!this.allowedImageTypes.includes(file.type)) {
        skippedUnsupportedFormat = true;
        continue;
      }

      try {
        file = await this.compressImage(file);
      } catch (error) {
        console.error('Error compresión:', error);
      }

      if (file.size > maxSize) {
        this.openInfoCard('La imagen no puede ocupar <br> más de 10 MB.');
        continue;
      }

      // Creamos un evento falso para el cropper sin dañar el DOM original
      const fakeEvent = {
        target: {
          files: [file],
        },
      };

      const result = await firstValueFrom(
        this.dialog
          .open(CropperImageComponent, {
            panelClass: 'info-dialog',
            disableClose: true,
            data: { img: fakeEvent, aspectRatio: 4 / 3 },
          })
          .afterClosed(),
      );

      if (result) {
        this.extraImages.push({
          file: result.object.base64,
          cropped: result.pathUrl,
        });
        this.eventInfo.patchValue({
          extraImages: this.extraImages,
        });
      }
    }

    if (skippedUnsupportedFormat) {
      this.openInfoCard(
        'Alguna imagen tenía un formato no admitido y no se ha subido. <br> Usa JPG, PNG, WEBP o GIF.',
      );
    }

    event.target.value = '';
  }

  async onExtraFile(event: any) {
    const reader = new FileReader();
    let filePath = '';
    if (event.target.files && event.target.files.length) {
      let file = event.target.files[0];
      const maxSize = 10 * 1024 * 1024; // 10 MB

      try {
        file = await this.compressImage(file);
      } catch (error) {}

      if (file.size > maxSize) {
        this.openInfoCard('El archivo no puede ocupar <br> más de 10 MB.');
        event.target.value = '';
        return;
      }

      let formData = new FormData();
      formData.append('file', file, file.name);

      if (file.type.match('text.*|application.*')) {
        reader.readAsDataURL(file);
        reader.onload = () => {
          var docSrc = reader.result as string;
          filePath = reader.result as string;
          this.eventInfo.patchValue({
            extraFile: docSrc,
          });
          this.extraFileControl = filePath;
        };
      } else {
        this.openInfoCard('El archivo debe ser un documento', true);
      }
    }
    if (!event.target.files || event.target.files.length === 0) {
      if (this.event && this.event.extraFile) {
        this.extraFileControl = null;
      }
    }
  }

  // Llamado por RouterCreateComponent tras guardar con éxito: el base64 de la portada
  // y del adjunto ya está en el servidor y dejarlo en el formulario haría que siguiera
  // contando como una subida pendiente (ver hasUnsavedChanges). Sin emitEvent para no
  // disparar otra vuelta de valueChanges sobre el estado que se acaba de fotografiar.
  clearPendingUploads() {
    this.eventInfo.patchValue({ file: '', extraFile: null }, { emitEvent: false });
  }

  // Tras guardar, el padre devuelve las fotos del carrusel tal y como han quedado en
  // el servidor (con id y sin base64). Hay que refrescarlas también aquí: cualquier
  // cambio en el formulario vuelca este estado sobre el del servicio (ver el
  // valueChanges de ngOnInit), así que si no, el base64 volvería y el siguiente
  // guardado subiría las mismas fotos otra vez.
  refreshExtraImages(images: any[]) {
    this.extraImages = images || [];
    this.eventInfo.patchValue({ extraImages: this.extraImages }, { emitEvent: false });
  }

  async compressImage(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      // Si NO es una imagen estándar (ej. un PSD o PDF), devolvemos el original sin comprimir
      if (!file.type.match(/image\/(jpeg|png|webp)/)) {
        resolve(file);
        return;
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event: any) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1080;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                resolve(file);
              }
            },
            'image/jpeg',
            0.7,
          );
        };
        img.onerror = (error) => reject(error);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  ngOnDestroy(): void {
    if (this.mapSub) {
      this.mapSub.unsubscribe();
    }
  }

  onBlurPlace() {
    setTimeout(() => {
      // Si el input tiene texto (es un string) pero la latitud sigue nula,
      // significa que escribió pero NO seleccionó una opción de la lista.
      if (
        this.lugar &&
        typeof this.lugar === 'string' &&
        !this.eventInfo.get('latitude')?.value
      ) {
        // 1. Limpiamos visualmente el input
        this.lugar = '';

        // 2. Limpiamos la data del formulario por seguridad
        this.eventInfo.patchValue({
          latitude: null,
          longitude: null,
          address: null,
          id_city: null,
        });

        // 3. Obligamos al usuario avisándole con tu modal
        this.openInfoCard(
          'Debes seleccionar una dirección del listado sugerido.',
          false,
          true,
        );
      }
    }, 300);
  }
}
